import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SafetyOutlined, ClockCircleOutlined, CalendarOutlined, SwapOutlined } from '@ant-design/icons';
import { AppHeader } from '@components/layout/AppHeader';
import { InfoCard } from '@components/ui/InfoCard/InfoCard';
import { SkeletonLoader, SkeletonDetailGrid } from '@components/ui/SkeletonLoader';
import { LocationCard } from '@components/ui/LocationCard';
import { useAuth } from '../../context/AuthContext';
import { useToastContext } from '../../context/ToastContext';
import { getUserById } from '../../api/user';
import { updateUserById } from '../../api/auth';
import { createPayment, type CreatePaymentInput, type SubscriptionPreview, type GetSubscriptionV2Output, getSubscriptionTransactions, calculateTotalAdjustment, selectAdjustmentsForPayment, formatPayableAmount, adjustmentSignedAmount, isCreditAdjustment, roundToPaise, MIN_PAYABLE_RUPEES, type Transaction, type ScheduledInstallment, type InstallmentWithTxn } from '../../api/payment';
import { computePaymentSummary, stampedAdjustmentAmount } from '../../utils/subscriptionHelpers';
import { getStudentById } from '../../api/students';
import { formatDate } from '../../utils/dateFormatter';
import { ROUTES } from '@utils/constants';
import { AdjustedAmount } from '../../components/subscription/AdjustedAmount';
import './styles/userdetails.css';

interface UserDetailsState {
  studentId: string;
  student: any;
  selectedPlan: any;
  paymentPlan?: any;
  subscriptionPreview?: SubscriptionPreview | GetSubscriptionV2Output;
  startDate?: string;
  termConfig?: any;
  distance?: number;
  duration?: number;
  transportType?: string;
  campuses?: any[];
  shifts?: any[];
  pickupLocation?: any;
  amountToPay?: number;
  selectedPricing?: {
    config: any;
    tier: any;
    capacity: string;
    capacityLabel: string;
  };
  estimatedPickupTime?: string | null;
  // Installment payment fields
  isInstallmentPayment?: boolean;
  installmentPayment?: {
    subscriptionId: string;
    installmentNumber: number;
    amountRupees: number;
  };
  // Transaction fields
  subscriptionId?: string;
  // Transaction IDs already stamped on installments/token — must never be reused
  usedTransactionIds?: string[];
}

const formatTripType = (pickupType?: string) => {
  if (pickupType === 'ONE_WAY') return 'One Way';
  return 'Two Way';
};

const getSubscriptionStartDate = (stateStartDate?: string, shiftValidFrom?: string | number): string | null => {
  // If state.startDate is available, use it
  if (stateStartDate) {
    return stateStartDate;
  }

  // Fallback to shift start date with date validation
  if (shiftValidFrom) {
    const shiftStartDate = new Date(shiftValidFrom);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    shiftStartDate.setHours(0, 0, 0, 0);

    // If today has crossed the shift start date, use tomorrow
    if (today > shiftStartDate) {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    }

    // Otherwise use the shift start date
    return shiftStartDate.toISOString().split('T')[0];
  }

  return null;
};

const SubscriptionSummaryCard: React.FC<{ state: UserDetailsState; onSubscriptionStartChange?: (date: string | null) => void; currentInstallmentNumber?: number; clampedDisplayAdj?: number; transactions?: Transaction[] }> = ({ state, onSubscriptionStartChange, currentInstallmentNumber, clampedDisplayAdj = 0, transactions = [] }) => {
  if (!state?.subscriptionPreview && !state?.selectedPlan) return null;

  const preview = state.subscriptionPreview;
  const pricing = state.selectedPricing;

  const shiftName = (preview?.shiftDetails as any)?.name;
  const shiftStartsAt = (preview?.shiftDetails as any)?.schedule?.startsAt;
  const shiftEndsAt = (preview?.shiftDetails as any)?.schedule?.endsAt;
  const subscriptionStart = getSubscriptionStartDate(state.startDate, preview?.shiftDetails?.validFrom);
  const subscriptionEnd = preview?.shiftDetails?.validUntil;

  // Notify parent component of the calculated subscription start date
  React.useEffect(() => {
    onSubscriptionStartChange?.(subscriptionStart);
  }, [subscriptionStart, onSubscriptionStartChange]);

  const tripType = formatTripType(pricing?.tier?.pickupType);
  const estimatedPickup = state.estimatedPickupTime;

  const pickupLocation = state.student?.geoAddress || state.student?.location?.address || 'Your registered address';
  
  // Get full campus address from student object (same logic as ReviewDetails)
  const getCampusAddress = (): {campusName: string | null, dropLocation: string | null} => {
    const campus = state.student?.campus;
    if (!campus) return {campusName: null, dropLocation: null};
    
    // Check if address is available in contact
    if (campus?.contact?.address) {
      const addr = campus.contact.address;
      const parts = [];
      if (addr.primaryAddressLine) parts.push(addr.primaryAddressLine);
      if (addr.secondaryAddressLine) parts.push(addr.secondaryAddressLine);
      if (addr.city) parts.push(addr.city);
      if (addr.state) parts.push(addr.state);
      if (addr.postalCode) parts.push(addr.postalCode);
      if (addr.country) parts.push(addr.country);
      return {campusName: campus?.name || null, dropLocation: parts.join(', ') || null};
    }
    return {campusName: null, dropLocation: null};
  };
  
  const {dropLocation, campusName} = getCampusAddress();

  const token = preview?.token;
  const installments: ScheduledInstallment[] = preview?.paymentCalendar?.installments ?? [];
  const totalAmount = (preview && 'totalAmount' in preview ? preview.totalAmount : undefined) ?? preview?.paymentCalendar?.totalAmount ?? 0;

  // Per-row paid adjustments + aggregate total both use shared helpers
  // (stampedAdjustmentAmount / computePaymentSummary) — no duplicated math.
  const allTxnsById = new Map(transactions.map(t => [t.id, t]));

  const isTokenPaid = !!currentInstallmentNumber;
  const tokenPostedAdj = isTokenPaid
    ? stampedAdjustmentAmount(
        (token ?? {}) as { transactionId?: string; transactionIds?: string[] },
        allTxnsById,
      )
    : 0;

  const instPostedAdjByNumber = new Map<number, number>(
    installments.map((inst) => {
      const num = inst.installmentNumber ?? -1;
      const isPaid = currentInstallmentNumber && num < currentInstallmentNumber;
      const adj = isPaid
        ? stampedAdjustmentAmount(inst as InstallmentWithTxn, allTxnsById)
        : 0;
      return [num, adj] as [number, number];
    })
  );

  const { adjustedTotal: effectiveTotal } = computePaymentSummary(
    token
      ? {
          amount: token.amount,
          paidAt: (token as { paidAt?: number }).paidAt,
          transactionId: (token as { transactionId?: string }).transactionId,
          transactionIds: (token as { transactionIds?: string[] }).transactionIds,
        }
      : null,
    totalAmount,
    installments,
    transactions,
  );

  return (
    <div className="subscription-summary">
      {/* SUBSCRIPTION DETAILS */}
      {(shiftName || shiftStartsAt || subscriptionStart || subscriptionEnd) && (
        <div className="subscription-summary__card">
          <span className="subscription-summary__section-label">Subscription Details</span>
          {shiftName && (
            <div className="subscription-summary__detail-row">
              <span className="subscription-summary__detail-label">
                <ClockCircleOutlined style={{ marginRight: 6 }} />Shift
              </span>
              <span className="subscription-summary__detail-value">{shiftName}</span>
            </div>
          )}
          {shiftStartsAt && shiftEndsAt && (
            <div className="subscription-summary__detail-row">
              <span className="subscription-summary__detail-label">
                <ClockCircleOutlined style={{ marginRight: 6 }} />Timings
              </span>
              <span className="subscription-summary__detail-value">{shiftStartsAt} – {shiftEndsAt}</span>
            </div>
          )}
          {subscriptionStart && (
            <div className="subscription-summary__detail-row">
              <span className="subscription-summary__detail-label">
                <CalendarOutlined style={{ marginRight: 6 }} />Start
              </span>
              <span className="subscription-summary__detail-value">{formatDate(subscriptionStart)}</span>
            </div>
          )}
          {subscriptionEnd && (
            <div className="subscription-summary__detail-row">
              <span className="subscription-summary__detail-label">
                <CalendarOutlined style={{ marginRight: 6 }} />End
              </span>
              <span className="subscription-summary__detail-value">{formatDate(subscriptionEnd)}</span>
            </div>
          )}
          <div className="subscription-summary__detail-row">
            <span className="subscription-summary__detail-label">
              <SwapOutlined style={{ marginRight: 6 }} />Trip type
            </span>
            <span className="subscription-summary__detail-value">{tripType}</span>
          </div>
          {estimatedPickup && (
            <div className="subscription-summary__detail-row">
              <span className="subscription-summary__detail-label">
                <ClockCircleOutlined style={{ marginRight: 6 }} />Est. pickup
              </span>
              <span className="subscription-summary__detail-value">{estimatedPickup}</span>
            </div>
          )}
        </div>
      )}

      {/* LOCATIONS */}
      {(pickupLocation || dropLocation) && <div className="subscription-summary__card">
        <span className="subscription-summary__section-label">Locations</span>
        <LocationCard
          homeAddress={pickupLocation}
          dropoffAddress={dropLocation}
          campusName={campusName}
          onEdit={() => {}}
        />
      </div>}

      {/* PAYMENT SCHEDULE */}
      {(token || installments.length > 0) && (
        <div className="subscription-summary__card">
          <span className="subscription-summary__section-label">Payment Schedule</span>
          {token && (() => {
            const tokenAdj = isTokenPaid ? tokenPostedAdj : clampedDisplayAdj;

            return (
              <div className="subscription-summary__schedule-item">
                {isTokenPaid ? (
                  <div style={{ fontSize: '18px', color: '#22c55e', fontWeight: 'bold', marginTop: '2px' }}>✓</div>
                ) : (
                  <div className="subscription-summary__schedule-dot subscription-summary__schedule-dot--orange"></div>
                )}
                <div className="subscription-summary__schedule-info">
                  <span className={`subscription-summary__schedule-label ${!isTokenPaid ? 'subscription-summary__schedule-label--orange' : ''}`}>
                    {isTokenPaid ? 'Paid' : 'Pay Now'}
                  </span>
                  {token.validUntil && (
                    <span className={`subscription-summary__schedule-date ${!isTokenPaid ? 'subscription-summary__schedule-date--orange' : ''}`}>Valid: {formatDate(token.validUntil)}</span>
                  )}
                </div>
                <AdjustedAmount
                  original={token.amount ?? 0}
                  adj={tokenAdj}
                  originalClassName={`subscription-summary__schedule-amount ${isTokenPaid ? 'subscription-summary__schedule-amount--green' : 'subscription-summary__schedule-amount--orange'}`}
                />
              </div>
            );
          })()}
          {installments.map((inst, idx) => {
            const instNum = inst.installmentNumber ?? -1;
            const isFirstInstallment = idx === 0;
            const isPaidInstallment = currentInstallmentNumber && instNum < currentInstallmentNumber;
            const isCurrentInstallment = currentInstallmentNumber === instNum;
            const label = token
              ? `Installment ${instNum}`
              : (isFirstInstallment ? 'Pay Now' : `Installment ${instNum}`);
            const isActive = isFirstInstallment && !token && !currentInstallmentNumber;

            const instAmount = inst.amount ?? 0;
            const instAdj = isPaidInstallment
              ? (instPostedAdjByNumber.get(instNum) ?? 0)
              : ((isCurrentInstallment || isActive) ? clampedDisplayAdj : 0);

            return (
              <div className="subscription-summary__schedule-item" key={instNum}>
                {isPaidInstallment ? (
                  <div style={{ fontSize: '18px', color: '#22c55e', fontWeight: 'bold', marginTop: '2px' }}>✓</div>
                ) : (
                  <div className={`subscription-summary__schedule-dot ${isCurrentInstallment ? 'subscription-summary__schedule-dot--orange' : isActive ? 'subscription-summary__schedule-dot--active' : ''}`}></div>
                )}
                <div className="subscription-summary__schedule-info">
                  <span className={`subscription-summary__schedule-label ${isCurrentInstallment ? 'subscription-summary__schedule-label--orange' : ''}`}>
                    {isPaidInstallment ? 'Paid' : (isCurrentInstallment ? 'Pay Now' : label)}
                  </span>
                  {inst.dueDate && (
                    <span className={`subscription-summary__schedule-date ${isCurrentInstallment ? 'subscription-summary__schedule-date--orange' : ''}`}>Due: {formatDate(inst.dueDate)}</span>
                  )}
                </div>
                <AdjustedAmount
                  original={instAmount}
                  adj={instAdj}
                  originalClassName={`subscription-summary__schedule-amount ${isPaidInstallment ? 'subscription-summary__schedule-amount--green' : isCurrentInstallment ? 'subscription-summary__schedule-amount--orange' : ''}`}
                />
              </div>
            );
          })}
          <div className="subscription-summary__total-row">
            <span className="subscription-summary__total-label">Total</span>
            <AdjustedAmount
              original={totalAmount}
              adj={effectiveTotal != null ? effectiveTotal - totalAmount : 0}
              originalClassName="subscription-summary__total-value"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as UserDetailsState;
  const { parentId } = useAuth();
  const { addToast } = useToastContext();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculatedSubscriptionStart, setCalculatedSubscriptionStart] = useState<string | null>(null);
  const [availableTransactions, setAvailableTransactions] = useState<any[]>([]);
  const [totalTransactionAmount, setTotalTransactionAmount] = useState(0);
  const [clampedDisplayAdj, setClampedDisplayAdj] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone_number: ""
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Fetch user details
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!parentId) {
        addToast({ message: 'User not authenticated', type: 'error' });
        navigate(ROUTES.WELCOME);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getUserById(parentId);
        setFormData({
          firstName: data?.givenName || '',
          lastName: data?.familyName || '',
          email: data['custom:unverifiedEmail'] || data.email || '',
          phone_number: data?.phone
        });
      } catch (error) {
        console.error('Error fetching user details:', error);
        addToast({ message: 'Failed to load user details', type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [parentId, navigate, addToast]);

  // Fetch applicable transactions for the subscription
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        let subscriptionId: string | undefined;


        // For new subscriptions
        if (!state?.isInstallmentPayment && state?.subscriptionPreview?.paymentPlan?.id) {
          subscriptionId = state?.subscriptionId || state?.student?.id;
        }
        // For installment payments
        else if (state?.isInstallmentPayment && state?.installmentPayment?.subscriptionId) {
          subscriptionId = state.installmentPayment.subscriptionId;
        }

        if (!subscriptionId) {
          return;
        }

        const transactions = await getSubscriptionTransactions(subscriptionId);
        setAvailableTransactions(transactions);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        addToast({ message: 'Unable to load discount information. Proceeding without adjustments.', type: 'warning' });
      }
    };

    fetchTransactions();
  }, [state?.subscriptionPreview?.paymentPlan?.id, state?.isInstallmentPayment, state?.subscriptionId, state?.student?.id, state?.installmentPayment?.subscriptionId]);

  // Net of all unused PENDING adjustments (CREDIT reduces, DEBIT increases),
  // matching the admin console cascade onto the installment being paid.
  useEffect(() => {
    const alreadyUsed = new Set(state?.usedTransactionIds ?? []);
    const pendingTransactions = availableTransactions.filter(
      t => t.status === 'PENDING' && !alreadyUsed.has(t.id)
    );
    setTotalTransactionAmount(
      pendingTransactions.length > 0 ? calculateTotalAdjustment(pendingTransactions) : 0,
    );
  }, [availableTransactions, state?.isInstallmentPayment, state?.installmentPayment, state?.usedTransactionIds]);

  // Calculate clamped display adjustment to match charge logic (respects ₹1 floor)
  useEffect(() => {
    const preview = state?.subscriptionPreview;
    const token = preview?.token;
    const installments: ScheduledInstallment[] = preview?.paymentCalendar?.installments ?? [];
    const currentInstallmentNumber = state?.installmentPayment?.installmentNumber;
    const isTokenPaid = !!currentInstallmentNumber;

    if (token && !isTokenPaid) {
      // Token is active — respect floor
      const baseAmount = token.amount ?? 0;
      const { payableAmount } = selectAdjustmentsForPayment(baseAmount, []);
      setClampedDisplayAdj(Math.max(payableAmount - baseAmount, totalTransactionAmount));
    } else if (!isTokenPaid && installments.length > 0) {
      // First installment is active — respect floor
      const baseAmount = installments[0]?.amount ?? 0;
      const { payableAmount } = selectAdjustmentsForPayment(baseAmount, []);
      setClampedDisplayAdj(Math.max(payableAmount - baseAmount, totalTransactionAmount));
    } else {
      setClampedDisplayAdj(totalTransactionAmount);
    }
  }, [state?.subscriptionPreview, totalTransactionAmount, state?.installmentPayment?.installmentNumber]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Apply alphabet-only validation for name fields (matching StudentDetails validation)
    let processedValue = value;
    if (name === 'firstName' || name === 'lastName') {
      processedValue = value.replace(/[^a-zA-Z\s]/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear error for this field on change
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.phone_number?.trim()) {
      errors.phone_number = 'Phone number is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContinue = async () => {
    // ── 1. Validate form fields ──────────────────────────────────────────
    if (!validateForm()) {
      addToast({ message: 'Please fix the errors before continuing', type: 'error' });
      return;
    }

    setIsSubmitting(true);

    try {
      // ── 2. Always update user details first ────────────────────────────
      const userAttributes: Array<{ Name: string; Value: string }> = [];

      if (formData.firstName.trim()) {
        userAttributes.push({ Name: 'given_name', Value: formData.firstName.trim() });
      }
      if (formData.lastName.trim()) {
        userAttributes.push({ Name: 'family_name', Value: formData.lastName.trim() });
      }
      if (formData.email.trim()) {
        userAttributes.push({ Name: 'custom:unverifiedEmail', Value: formData.email.trim() });
      }

      if (userAttributes.length > 0) {
        addToast({ message: 'Updating your details...', type: 'info' });
        await updateUserById(userAttributes);
      }

      // ── 3. Determine subscription start date ──────────────────────────
      const distanceNum = state.distance ? parseFloat(state.distance.toFixed(1)) : 0;
      const productInfo = state.student?.shifts?.[0];

      let subscriptionStartDate: string | undefined;

      if (calculatedSubscriptionStart) {
        subscriptionStartDate = typeof calculatedSubscriptionStart === 'number'
          ? new Date(calculatedSubscriptionStart).toISOString().split('T')[0]
          : calculatedSubscriptionStart;
      } else if (state.startDate) {
        subscriptionStartDate = typeof state.startDate === 'number'
          ? new Date(state.startDate).toISOString().split('T')[0]
          : state.startDate;
      } else {
        const studentDetails = await getStudentById(state.student.id);
        if (studentDetails?.startDate) {
          subscriptionStartDate = typeof studentDetails.startDate === 'number'
            ? new Date(studentDetails.startDate).toISOString().split('T')[0]
            : studentDetails.startDate;
        }
      }

      // ── 4. Create payment ─────────────────────────────────────────────
      // Amounts + ID sets are computed from page-load data. Concurrency safety
      // rests on the server: CreatePayment rejects already-stamped IDs and
      // recomputes the effective installment amount (PaymentAccessor).
      if (state?.isInstallmentPayment && state?.installmentPayment) {
        // Installment payment flow — multi-adjustment (PAY-6)
        addToast({ message: 'Initiating installment payment...', type: 'info' });

        const alreadyUsed = new Set(state?.usedTransactionIds ?? []);
        const pendingTransactions = availableTransactions.filter(
          t => t.status === 'PENDING' && !alreadyUsed.has(t.id),
        );

        const installmentBaseAmount = state.installmentPayment.amountRupees;
        const { selected, deferred, payableAmount } = selectAdjustmentsForPayment(
          installmentBaseAmount,
          pendingTransactions,
        );

        if (deferred.length > 0) {
          const deferredCredit = Math.abs(calculateTotalAdjustment(deferred));
          addToast({
            message:
              deferredCredit > 0
                ? `₹${deferredCredit.toLocaleString('en-IN')} credit exceeds this installment and will apply on a later payment.`
                : 'Some adjustments could not fit this installment and will apply later.',
            type: 'warning',
          });
        }

        const paymentPayload: CreatePaymentInput = {
          subscriberId: parentId || '',
          granteeId: state.studentId,
          email: formData.email,
          phone: formData?.phone_number || '',
          amount: formatPayableAmount(payableAmount),
          productInfo,
          paymentPlanId: state?.subscriptionPreview?.paymentPlan?.id,
          subscriptionStartDate,
          subscriptionV2Id: state.installmentPayment.subscriptionId,
          subscriptionV2InstallmentNumber: state.installmentPayment.installmentNumber,
          tags: state?.student?.tags ?? {
            plan: state.selectedPlan?.name || "",
            distanceToCampus: distanceNum,
          },
          ...(selected.length > 0 && { transactionIds: selected.map(t => t.id) }),
        };

        const paymentData = await createPayment(paymentPayload);
        localStorage.setItem('currentPaymentId', paymentData.id);

        navigate(ROUTES.PAYMENT_PROCESSING, {
          state: {
            studentId: state.studentId,
            student: state.student,
            isInstallmentPayment: true,
            installmentPayment: state.installmentPayment,
            paymentResponse: paymentData,
          },
        });
      } else {
        // New subscription flow — singular transactionId only (create-subscription
        // stamps the token from that field; sending transactionIds[] while reducing
        // the charge would risk double-application if the array were ignored).
        if (!state?.selectedPlan || !state?.subscriptionPreview) {
          console.error('[UserDetails] Missing required state:', {
            hasSelectedPlan: !!state?.selectedPlan,
            hasSubscriptionPreview: !!state?.subscriptionPreview,
          });
          addToast({ message: 'No plan selected', type: 'error' });
          return;
        }

        addToast({ message: 'Initiating payment...', type: 'info' });

        const alreadyUsed = new Set(state?.usedTransactionIds ?? []);
        const pendingTransactions = availableTransactions.filter(
          t => t.status === 'PENDING' && !alreadyUsed.has(t.id),
        );

        const subscriptionBaseAmount = state?.amountToPay || 0;
        const { selected, deferred } = selectAdjustmentsForPayment(
          subscriptionBaseAmount,
          pendingTransactions,
        );
        // One adjustment max on create — matches legacy create-subscription stamp path.
        // Prefer a credit that fits alone so the singular stamp reduces the charge;
        // otherwise take selected[0] (debits-first from the selector).
        const primary =
          selected.find(
            t =>
              isCreditAdjustment(t) &&
              subscriptionBaseAmount + adjustmentSignedAmount(t) >= MIN_PAYABLE_RUPEES,
          ) ?? selected[0];
        const leftover = [
          ...selected.filter(t => t.id !== primary?.id),
          ...deferred,
        ];
        const newSubscriptionFinalAmount = Math.max(
          MIN_PAYABLE_RUPEES,
          primary
            ? roundToPaise(subscriptionBaseAmount + adjustmentSignedAmount(primary))
            : roundToPaise(subscriptionBaseAmount),
        );

        if (leftover.length > 0) {
          const leftoverCredits = leftover.filter(isCreditAdjustment);
          const leftoverDebits = leftover.filter(t => !isCreditAdjustment(t));
          if (leftoverCredits.length > 0 && leftoverDebits.length === 0) {
            const leftoverCredit = Math.abs(calculateTotalAdjustment(leftoverCredits));
            addToast({
              message: `₹${leftoverCredit.toLocaleString('en-IN')} remaining credit will apply on a later installment payment.`,
              type: 'warning',
            });
          } else if (leftoverDebits.length > 0 && leftoverCredits.length === 0) {
            const leftoverDebit = calculateTotalAdjustment(leftoverDebits);
            addToast({
              message: `₹${leftoverDebit.toLocaleString('en-IN')} remaining debit will apply on a later installment payment.`,
              type: 'warning',
            });
          } else {
            addToast({
              message: 'Some remaining adjustments will apply on a later installment payment.',
              type: 'warning',
            });
          }
        }

        const paymentPayload: CreatePaymentInput = {
          subscriberId: parentId || '',
          granteeId: state.student.id,
          email: formData.email,
          phone: formData?.phone_number || '',
          amount: formatPayableAmount(newSubscriptionFinalAmount),
          productInfo,
          paymentPlanId: state.selectedPlan.id,
          subscriptionStartDate,
          tags: {
            plan: state.selectedPlan.name || "",
            distanceToCampus: distanceNum,
          },
          ...(primary && { transactionId: primary.id }),
        };

        const paymentData = await createPayment(paymentPayload);
        localStorage.setItem('currentPaymentId', paymentData.id);

        navigate(ROUTES.PAYMENT_PROCESSING, {
          state: {
            studentId: state.studentId,
            student: state.student,
            selectedPlan: state.selectedPlan,
            subscriptionPreview: state.subscriptionPreview,
            distance: state.distance,
            paymentResponse: paymentData,
          },
        });
      }

      addToast({ message: 'Proceeding to payment...', type: 'success' });
    } catch (error: any) {
      console.error('[UserDetails] Error in payment flow:', {
        error: error.message,
        stack: error.stack,
        response: error.response?.data,
      });
      addToast({
        message: error.message || 'Failed to proceed with payment',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="user-details-page">
        <AppHeader showBack onBack={() => navigate(-1)} />
        <div className="user-details-page__container">
          <div className="user-details-page__content">
            {/* Title Section Shimmer */}
            <div className="user-details-page__title-section">
              <SkeletonLoader type="text" width="60%" height="32px" />
              <div style={{ marginTop: '12px' }}>
                <SkeletonLoader type="text" width="100%" height="16px" />
              </div>
            </div>

            {/* Plan Section Shimmer */}
            <div className="user-details-page__plan-section">
              <div className="user-details-page__card">
                <SkeletonLoader type="text" width="40%" height="16px" />
                <div style={{ marginTop: '16px' }}>
                  <SkeletonLoader type="text" width="50%" height="20px" />
                  <div style={{ marginTop: '8px' }}>
                    <SkeletonLoader type="text" width="70%" height="14px" />
                  </div>
                </div>
                <div style={{ marginTop: '16px' }}>
                  <SkeletonDetailGrid count={3} />
                </div>
              </div>
            </div>

            {/* Form Section Shimmer */}
            <div className="user-details-page__form">
              {/* Name Card Shimmer */}
              <div className="user-details-page__form-group">
                <div className="user-details-page__card">
                  <SkeletonLoader type="text" width="40%" height="16px" />
                  <div className="user-details-page__name-row" style={{ marginTop: '12px' }}>
                    <SkeletonLoader type="input" width="100%" height="40px" />
                    <SkeletonLoader type="input" width="100%" height="40px" />
                  </div>
                </div>
              </div>

              {/* Email Card Shimmer */}
              <div className="user-details-page__form-group">
                <div className="user-details-page__card">
                  <SkeletonLoader type="text" width="40%" height="16px" />
                  <div style={{ marginTop: '12px' }}>
                    <SkeletonLoader type="input" width="100%" height="40px" />
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <SkeletonLoader type="text" width="80%" height="12px" />
                  </div>
                </div>
              </div>

              {/* Phone Card Shimmer */}
              <div className="user-details-page__form-group">
                <div className="user-details-page__card">
                  <SkeletonLoader type="text" width="40%" height="16px" />
                  <div style={{ marginTop: '12px' }}>
                    <SkeletonLoader type="input" width="100%" height="40px" />
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <SkeletonLoader type="text" width="80%" height="12px" />
                  </div>
                </div>
              </div>
            </div>

            {/* Privacy Card Shimmer */}
            <div style={{ marginTop: '24px' }}>
              <SkeletonLoader type="card" width="100%" height="120px" />
            </div>

            {/* Button Shimmer */}
            <div style={{ marginTop: '24px' }}>
              <SkeletonLoader type="button" width="100%" height="48px" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-details-page">
      <AppHeader showBack onBack={() => navigate(-1)} />

      <div className="user-details-page__container">
        <div className="user-details-page__content">
          {/* Title Section */}
          <div className="user-details-page__title-section">
            <h1 className="user-details-page__title">Tell us about you</h1>
            <p className="user-details-page__subtitle">
              We use this information to contact you about your child and to send invoices and updates
            </p>
          </div>

          {/* Form Section */}
          <div className="user-details-page__form">
            {/* Parent/Guardian Name Card */}
            <div className="user-details-page__form-group">
              <div className="user-details-page__card">
                <label className="user-details-page__card-label">Parent/Guardian Name</label>

                {/* First Name and Last Name Fields Side by Side */}
                <div className="user-details-page__name-row">
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="First name"
                      disabled={isSubmitting}
                      className={`user-details-page__input ${formErrors.firstName ? 'user-details-page__input--error' : ''} ${isSubmitting ? 'user-details-page__input--disabled' : ''}`}
                    />
                    {formErrors.firstName && <p className="user-details-page__field-error">{formErrors.firstName}</p>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Last name"
                      disabled={isSubmitting}
                      className={`user-details-page__input ${formErrors.lastName ? 'user-details-page__input--error' : ''} ${isSubmitting ? 'user-details-page__input--disabled' : ''}`}
                    />
                    {formErrors.lastName && <p className="user-details-page__field-error">{formErrors.lastName}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Email Card */}
            <div className="user-details-page__form-group">
              <div className="user-details-page__card">
                <label className="user-details-page__card-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  disabled={isSubmitting}
                  className={`user-details-page__input ${formErrors.email ? 'user-details-page__input--error' : ''} ${isSubmitting ? 'user-details-page__input--disabled' : ''}`}
                />
                {formErrors.email
                  ? <p className="user-details-page__field-error">{formErrors.email}</p>
                  : <p className="user-details-page__field-hint">Invoice and payment updates will be sent here</p>
                }
              </div>
            </div>

            {/* Phone Number Card (Disabled) */}
            <div className="user-details-page__form-group">
              <div className="user-details-page__card">
                <label className="user-details-page__card-label">Phone Number</label>
                <input
                  type="tel"
                  value={formData?.phone_number || ''}
                  disabled
                  className="user-details-page__input user-details-page__input--disabled"
                />
                <p className="user-details-page__field-hint">
                  Used for important trip and safety updates
                </p>
              </div>
            </div>
          </div>

          {/* Subscription Summary - Show for both new subscriptions and installment payments */}
          {!state?.isInstallmentPayment && (
            <SubscriptionSummaryCard state={state} onSubscriptionStartChange={setCalculatedSubscriptionStart} clampedDisplayAdj={clampedDisplayAdj} transactions={availableTransactions} />
          )}

          {state?.isInstallmentPayment && state?.subscriptionPreview && (
            <SubscriptionSummaryCard
              state={{
                ...state,
                subscriptionPreview: state.subscriptionPreview,
                selectedPricing: {
                  config: {},
                  tier: state.subscriptionPreview.paymentPlan || {},
                  capacity: '',
                  capacityLabel: '',
                },
              }}
              onSubscriptionStartChange={setCalculatedSubscriptionStart}
              currentInstallmentNumber={state.installmentPayment?.installmentNumber}
              clampedDisplayAdj={clampedDisplayAdj}
              transactions={availableTransactions}
            />
          )}

          {/* Privacy Protection Card */}
          <InfoCard
            icon={<SafetyOutlined style={{ color: '#f5a623', fontSize: '24px' }} />}
            title="Privacy Protected"
            description="Your information is encrypted and only used for transportation services. We never share data with third parties."
            className="user-details-page__privacy-card"
          />

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            className={`user-details-page__continue-btn ${isSubmitting ? 'btn-loading' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="btn-loader btn-loader--dark"></span>
                Processing...
              </>
            ) : (
              state?.isInstallmentPayment ? 'Pay Now' : 'Continue to Payment'
            )}
          </button>

          {/* Loading Modal Overlay */}
          {isSubmitting && (
            <div className="user-details-page__modal-overlay">
              <div className="user-details-page__modal">
                <span className="btn-loader btn-loader--dark" style={{ width: 40, height: 40 }}></span>
                <h3>Processing</h3>
                <p>Please wait while we initiate your payment...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
